import React, { FunctionComponent } from "react";
import { Node, Edge, useReactFlow, useUpdateNodeInternals } from "reactflow";
import { IDataColumnDefProps, ITextQuery, PickerInput } from "@plex/react-components";
import { INodeConfigPropertiesProps } from "../NodeTypes/PropertyTypeDefinitions";
import {
  emptyObjectSchemaId,
  getDataTypeFromSchema,
  getSchemas,
  IDesignerSchema,
  listOfEmptyObjectSchemaId,
  noReferenceSchemaId
} from "../NodeTypes/DataSchemas";
import { INodeProperty, ISchemaPickerOptions, ISchemaPropertyPickerOptions } from "../NodeTypes/Base";
import {
  getSchemaObjectName,
  getSystemLabel,
  isCompositeIdSchema,
  isReferenceSchema
} from "../FlowDocument/FlowDocumentModel";
import { camelCaseToReadable } from "../Util/NodeUtil";
import { DataType, isPrimitiveLike } from "../NodeTypes/TypeDefinitions";
import { NodeConfigPropertyType } from "../FlowDocument/PropertyTypeDefinitions";
import { getPickerSchemaProperties } from "./SchemaPropertyPickerField";
import { createDataParamsFromSchema, deleteEdgesFromNodes } from "../Util/EdgeUtil";

interface ISchemaItem {
  id: string;
  name: string;
  system: string;
  dataType: string;
}

export interface ISchemaPickerProps extends INodeConfigPropertiesProps {
  dialogTitle: string;
  displayName: string;
}

export const setLinkedSchemaPropertySelectionToDefault = (node: Node, propertyName: string): string | undefined => {
  let firstProperty: string | undefined = undefined;
  const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node!.type!);
  const linkedSchemaProperties = nodeDefinition?.nodeConfigProperties.filter(
    (configProperty: INodeProperty) =>
      configProperty.propertyType === NodeConfigPropertyType.SchemaProperty &&
      (configProperty.options as ISchemaPropertyPickerOptions)?.schemaIdProperty === propertyName
  );

  linkedSchemaProperties?.forEach((property: INodeProperty) => {
    const propertyOptions = property.options as ISchemaPropertyPickerOptions;
    const pickerProperties = getPickerSchemaProperties(node, propertyOptions);
    const firstPickerProperty = pickerProperties.length > 0 ? pickerProperties[0] : undefined;
    if (propertyOptions?.displayProperty) {
      node!.data.nodeProperties[propertyOptions.displayProperty] = firstPickerProperty?.name;
    }
    node!.data.nodeProperties[property.name] = firstPickerProperty?.id;
    firstProperty = firstPickerProperty?.id;
  });

  return firstProperty;
};

export const SchemaPickerField: FunctionComponent<ISchemaPickerProps> = (props) => {
  const reactFlowInstance = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const options = props.options as ISchemaPickerOptions;
  let initialDisplay = "";
  if (options?.displayProperty) {
    initialDisplay = props.node!.data.nodeProperties[options.displayProperty];
  }
  let [{ pickerSelected, pickerData, searchText }, setPicker] = React.useState({
    pickerSelected: props.node?.data.nodeProperties[props.name]
      ? [
          {
            id: props.node?.data.nodeProperties[props.name],
            name: initialDisplay
          } as ISchemaItem
        ]
      : ([] as ISchemaItem[]),
    pickerData: [] as { id: string; name: string }[],
    searchText: ""
  });

  const getPickerSchemas = () => {
    const options = props.options as ISchemaPickerOptions;
    return getSchemas()
      .filter(
        (schema: IDesignerSchema) =>
          schema.id !== noReferenceSchemaId &&
          schema.id !== emptyObjectSchemaId &&
          schema.id !== listOfEmptyObjectSchemaId &&
          (!options?.referenceOnly || isReferenceSchema(schema)) &&
          (options?.includePrimitives || !isPrimitiveLike(getDataTypeFromSchema(schema.id) as DataType))
      )
      .map((schema: IDesignerSchema) => {
        const systemLabel = getSystemLabel(schema);
        const objectName = getSchemaObjectName(schema);
        let itemName = objectName ?? schema.id;
        const dataType = getDataTypeFromSchema(schema.id);
        const idLabelCompositeSuffix = isCompositeIdSchema(schema) ? " (Composite ID)" : "";
        let dataTypeName = camelCaseToReadable(dataType) + idLabelCompositeSuffix;
        if (dataType === DataType.OBJECTREFERENCE) {
          dataTypeName = "Reference";
        }
        return { id: schema.id, name: itemName, system: systemLabel, dataType: dataTypeName };
      });
  };

  const handlePickerSearch = (query: ITextQuery, cols: Array<IDataColumnDefProps<ISchemaItem>>) => {
    const results: ISchemaItem[] = getPickerSchemas();

    let items: {
      id: string;
      name: string;
    }[];
    if (cols.length === 0) {
      items = results.filter((x) => (x.name || "").toLowerCase().includes(query.query.toLowerCase()));
    } else {
      items = results.filter((row: any) =>
        query.isMatch(cols.map((col) => (col.valueSelector ? String(col.valueSelector(row)) : "")))
      );
    }

    setPicker((x: any) => ({ ...x, pickerData: items }));
  };

  const handlePickerSearchTextChanged = (value: string) => {
    setPicker((x: any) => ({ ...x, searchText: value }));
  };

  return (
    <PickerInput<ISchemaItem>
      key={props.name + "_schema_picker"}
      dialogTitle="Document Schemas"
      initialWidth={900}
      disabled={false}
      selected={
        props.node!.data.nodeProperties[props.name]
          ? [
              {
                id: props.node!.data.nodeProperties[props.name],
                name: props.node!.data.nodeProperties[options.displayProperty]
              }
            ]
          : []
      }
      onSelectionChanged={(values: any) => {
        if (props.node!.data.nodeProperties[props.name] === values[0]?.id) {
          return;
        }

        let currentEdges: Edge[] = reactFlowInstance.getEdges();

        if (options.linkedOutput) {
          currentEdges = deleteEdgesFromNodes(
            [props.node.id],
            reactFlowInstance,
            updateNodeInternals,
            {
              outputEdges: true,
              inputEdges: false,
              controlEdges: false
            },
            currentEdges
          );
        }

        const updateInputsFromSchema = (firstIdentifier: string | undefined, clearInputs?: boolean) => {
          const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(props.node!.type!)!;
          if (nodeDefinition.inputsSchemaProperties?.schemaPropertyName === props.name) {
            currentEdges = deleteEdgesFromNodes(
              [props.node.id],
              reactFlowInstance,
              updateNodeInternals,
              {
                outputEdges: false,
                inputEdges: true,
                controlEdges: false
              },
              currentEdges
            );
            if (clearInputs) {
              props.node.data.nodeProperties.inputs = {};
            } else {
              props.node.data.nodeProperties.inputs = createDataParamsFromSchema(
                props.node,
                values[0]?.id,
                true,
                reactFlowInstance,
                firstIdentifier
              );
            }
          }
        };

        if (values.length === 0) {
          setPicker((x) => ({ ...x, pickerSelected: [], searchText: "" }));
          [];
          props.node!.data.nodeProperties[props.name] = null;
          if (options?.displayProperty) {
            props.node!.data.nodeProperties[options.displayProperty] = null;
          }

          if (options.linkedOutput) {
            props.node.data.nodeProperties.outputs[options.linkedOutput].schemaId = noReferenceSchemaId;
          }
          const firstIdentifier = setLinkedSchemaPropertySelectionToDefault(props.node, props.name);
          updateInputsFromSchema(firstIdentifier, true);

          if (props.onChange) {
            props.onChange();
          }

          props.forceNodeUpdate();

          return;
        }

        pickerSelected = values.length > 0 ? [values[0]] : [];
        setPicker((x) => ({
          ...x,
          pickerSelected: pickerSelected,
          searchText: ""
        }));
        props.node!.data.nodeProperties[props.name] = values[0].id;
        if (options?.displayProperty) {
          props.node!.data.nodeProperties[options.displayProperty] = values[0].name;
        }

        if (options.linkedOutput) {
          props.node.data.nodeProperties.outputs[options.linkedOutput].schemaId = values[0]?.id;
        }
        const firstIdentifier = setLinkedSchemaPropertySelectionToDefault(props.node, props.name);
        updateInputsFromSchema(firstIdentifier);

        if (props.onChange) {
          props.onChange();
        }

        props.forceNodeUpdate();
      }}
      keySelector={(row: ISchemaItem) => row?.id}
      displaySelector={(row: ISchemaItem) => row?.name}
      data={pickerData}
      onSearch={handlePickerSearch}
      searchDisabled={false}
      isOpen={false}
      multiSelect={false}
      maxDisplay={500}
      searchText={searchText}
      onSearchTextChange={handlePickerSearchTextChanged}
    >
      <PickerInput.Column<ISchemaItem>
        key="1"
        id={props.displayName}
        title="Name"
        valueSelector={(row: ISchemaItem) => row.id}
        searchable
      >
        {(row: ISchemaItem) => row.name}
      </PickerInput.Column>
      <PickerInput.Column<ISchemaItem> key="2" id="system" title="System" valueSelector={(row: ISchemaItem) => row.id}>
        {(row: ISchemaItem) => row.system}
      </PickerInput.Column>
      <PickerInput.Column<ISchemaItem> key="2" id="type" title="Type" valueSelector={(row: ISchemaItem) => row.id}>
        {(row: ISchemaItem) => row.dataType}
      </PickerInput.Column>
    </PickerInput>
  );
};
